import React, { useEffect } from "react";

import { Navigate, useNavigate, useParams } from "react-router-dom";

import { toast } from "react-toastify";
import Text from "../../utils/commonComponent/Custom/text";
import CommonForm from "../../utils/commonComponent/commonForm";
import {
  CancelButton,
  SaveAndUpdate,
} from "../../utils/commonComponent/Custom/Button";
import {
  fetchByTable,
  fetchQuery,
} from "./../../utils/commonComponent/fetched";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL, DOMAIN } from "../../global";
import ListView from "../../utils/commonComponent/Custom/listView";
import { submitSiteDelete } from "../../utils/commonComponent/delete";
import LoaderModal from "../../utils/loader.js/loader";
import {
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getPermissions } from "../rbac/hasAccess";
import { validateRequiredFields } from "../../utils/commonComponent/errorAlert";
import AppConfirmBox from "../../utils/commonComponent/appConfirmBox";

const headers = [
  "id",
  "employeeName",
  "employeesCode",
  "leaveType",
  "fromDate",
  "toDate",
  "days",
  "remark",
  "applyLeaveDate",
  "leaveStatus",
];

const approveHeaders = [
  "approveManagerStatus",
  "approveLevel1",
  "approveLevel2",
  "rejectRemark",
];

const leaveOptions = [
  { id: 1, label: "Self Leave" },
  { id: 2, label: "Leave Request" },
];

// Suppose company has Sat & Sun as week offs for calculateAvailableBalance
export const weekOffs = ["Sunday"];

export const leaveTypeOptions = [
  { id: 1, name: "Sick Leave", base: 7, gender: "Both" },
  { id: 2, name: "Casual Leave", base: 12, gender: "Both" },
  { id: 3, name: "Privileged Leave", base: "DYNAMIC", gender: "Both" },
  { id: 4, name: "Maternity Leave", base: 60, gender: "Female" },
  { id: 5, name: "Paternity Leave", base: 2, gender: "Male" },
  { id: 7, name: "Menstrual Leave", base: "MONTHLY_1", gender: "Female" },
];

export const fetchPrivilegedLeaveByEmployee = async (_employeeId) => {
  try {
    /* -------------------------------------------------- */
    /* 1️⃣ Get base Privileged Leave */
    /* -------------------------------------------------- */
    const empRes = await fetchQuery(
      `SELECT IFNULL(PrivilegedLeaveBase, 0) AS basePL
       FROM employees
       WHERE id = ${_employeeId}`,
    );

    const basePL = Number(empRes?.[0]?.basePL || 0);

    /* -------------------------------------------------- */
    /* 2️⃣ Get attendance records */
    /* -------------------------------------------------- */
    const attendanceRes = await fetchQuery(
      `SELECT checkinDatetime
       FROM attendance
       WHERE employeeId = ${_employeeId}`,
    );

    /* -------------------------------------------------- */
    /* 3️⃣ Get holidays (status = 1) */
    /* -------------------------------------------------- */
    const holidayRes = await fetchQuery(
      `SELECT date
       FROM holidayRegister
       WHERE status = 1`,
    );

    // Fast lookup set → yyyy-mm-dd
    const holidaySet = new Set(
      holidayRes.map((h) => new Date(h.date).toISOString().split("T")[0]),
    );

    /* -------------------------------------------------- */
    /* 4️⃣ Count working days (exclude week offs + holidays) */
    /* -------------------------------------------------- */
    const workingDays = attendanceRes.filter((row) => {
      const dateObj = new Date(row.checkinDatetime);

      const dayName = dateObj.toLocaleDateString("en-US", {
        weekday: "long",
      });

      const dateKey = dateObj.toISOString().split("T")[0];

      // ❌ exclude week off
      if (weekOffs.includes(dayName)) return false;

      // ❌ exclude holiday
      if (holidaySet.has(dateKey)) return false;

      return true;
    }).length;

    /* -------------------------------------------------- */
    /* 5️⃣ Calculate earned PL */
    /* -------------------------------------------------- */
    const earnedPL = Math.floor(workingDays / 20);

    /* -------------------------------------------------- */
    /* 6️⃣ Total Privileged Leave */
    /* -------------------------------------------------- */
    const totalPL = basePL + earnedPL;

    return {
      basePL,
      workingDays,
      earnedPL,
      totalPL,
    };
  } catch (error) {
    console.error("Error fetching Privileged Leave:", error);
    return {
      basePL: 0,
      workingDays: 0,
      earnedPL: 0,
      totalPL: 0,
    };
  }
};

export async function calculateAvailableBalance(
  leaveType,
  leaveManagementDetails,
  weekOffs = [],
  fromDate,
  toDate,
  _employeeId,
  fromLeaveValue,
  toLeaveValue,
) {
  // -----------------------------
  // 1️⃣ Resolve base
  // -----------------------------
  let base = 0;
  let isMonthly = false;

  const leaveConfig = leaveTypeOptions.find((l) => l.name === leaveType);

  if (leaveConfig) {
    if (leaveConfig.base === "DYNAMIC") {
      const plData = await fetchPrivilegedLeaveByEmployee(_employeeId);
      base = plData?.totalPL || 0;
    } else if (leaveConfig.base === "MONTHLY_1") {
      // Menstrual leave: 1 day per month, no carry-forward
      base = 1;
      isMonthly = true;
    } else {
      base = leaveConfig.base || 0;
    }
  }

  // -----------------------------
  // 2️⃣ Used balance (approved)
  // -----------------------------
  let used = 0;

  if (isMonthly) {
    // For monthly leave, only count usage within the *current calendar month*
    const now = fromDate ? new Date(fromDate) : new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    used = leaveManagementDetails
      .filter((l) => {
        if (
          l.employeeId != _employeeId ||
          l.leaveType !== leaveType ||
          !["Approved", "Pending"].includes(l.leaveStatus)
        )
          return false;

        // Check if the leave falls in the same month & year
        const leaveDate = new Date(l.fromDate || l.date);
        return (
          leaveDate.getFullYear() === currentYear &&
          leaveDate.getMonth() === currentMonth
        );
      })
      .reduce((sum, l) => sum + (l.days || 0), 0);
  } else {
    used = leaveManagementDetails
      .filter(
        (l) =>
          l.employeeId == _employeeId &&
          l.leaveType === leaveType &&
          ["Approved", "Pending"].includes(l.leaveStatus),
      )
      .reduce((sum, l) => sum + (l.days || 0), 0);
  }

  // -----------------------------
  // 3️⃣ Applying days calculation
  // -----------------------------
  let applyingDays = 0;

  if (fromDate && toDate) {
    const start = new Date(fromDate);
    const end = new Date(toDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (!weekOffs.includes(d.toLocaleString("en-US", { weekday: "long" }))) {
        applyingDays++;
      }
    }

    // Half-day logic
    if (fromDate === toDate) {
      if (
        fromLeaveValue === "First Half Day" ||
        fromLeaveValue === "Second Half Day"
      ) {
        applyingDays = 0.5;
      }
    } else {
      if (
        fromLeaveValue === "First Half Day" ||
        fromLeaveValue === "Second Half Day"
      ) {
        applyingDays -= 0.5;
      }
      if (
        toLeaveValue === "First Half Day" ||
        toLeaveValue === "Second Half Day"
      ) {
        applyingDays -= 0.5;
      }
    }
  }

  // -----------------------------
  // 4️⃣ Final balance
  // -----------------------------
  const availableBalance = Math.max(base - used, 0);

  return { availableBalance, applyingDays };
}

const ApplyLeaveUI = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const permissions = getPermissions(props.moduleId);
  const [inputStates, setInputStates] = React.useState({
    applyLeaveDate: new Date(),
  });
  const [leaveManagementDetails, setLeaveManagementDetails] = React.useState(
    [],
  );
  const [leaveData, setLeaveData] = React.useState({
    availableBalance: 0,
    applyingDays: 0,
  });

  const [downloadPdfloading, setDownlaodPdfLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [employeeData, setEmployeeData] = React.useState({});
  const [holidayList, setHolidayList] = React.useState([]);
  const [pendingStatus, setPendingStatus] = React.useState(true);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = React.useState(() => {
    const storedValue = localStorage.getItem("selectedLeaveTypeId");
    return storedValue ? Number(storedValue) : 1;
  });

  const _employeeId = Number(localStorage.getItem("employeeId"));
  const _userType = Number(localStorage.getItem("type"));
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [dialogProps, setDialogProps] = React.useState({});
  const [remark, setRemark] = React.useState("");
  const remarkRef = React.useRef("");

  const handleEdit = (row) => navigate(`/leave/edit/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("leaveManagement", row.id);

  const inputFields = [
    // { type: "TextField", label: "Document No", stateKey: "documentNo" },
    {
      type: "DatePicker",
      label: "Date of Application",
      stateKey: "applyLeaveDate",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Leave Type",
      stateKey: "leaveType",
      options: [
        { id: 1, name: "Sick Leave" },
        { id: 2, name: "Casual Leave" },
        { id: 3, name: "Privileged Leave" },

        ...(employeeData?.gender === "Male"
          ? [{ id: 5, name: "Paternity Leave" }]
          : employeeData?.gender === "Female"
            ? [
                { id: 4, name: "Maternity Leave" },
                { id: 6, name: "Menstrual Leave" },
              ]
            : []),
      ],
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Available Balance",
      stateKey: "leaveBalance", //availableBalance
      disabled: true,
    },
    {
      type: "DatePicker",
      label: "From Date",
      stateKey: "fromDate",
      disablePastDates: true,
    },
    {
      type: "Autocomplete",
      label: "Leave Value",
      stateKey: "fromLeaveValue",
      options: [
        { id: 1, name: "Full Day" },
        { id: 2, name: "First Half Day" },
        { id: 3, name: "Second Half Day" },
      ],
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "DatePicker",
      label: "Till Date",
      stateKey: "toDate",
      disablePastDates: true,
      minimumDate: inputStates?.fromDate,
    },
    {
      type: "Autocomplete",
      label: "Leave Value",
      stateKey: "toLeaveValue",
      options: [
        { id: 1, name: "Full Day" },
        { id: 2, name: "First Half Day" },
        { id: 3, name: "Second Half Day" },
      ],
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    { type: "TextField", label: "Remarks", stateKey: "remark" },
    { type: "ImageUpload", label: "Document", stateKey: "docUrl" },
    {
      type: "Autocomplete",
      label: "Employee Name",
      stateKey: "employeeId",
      options: "employees",
      valueKey: "id",
      getOptionLabelKey: "name",
      optionsFilter: `${
        _employeeId ? `where id=${_employeeId} and status = 1` : ""
      }`,
    },
  ];

  const updateInputObject = (key, value) => {
    setInputStates((prevInputStates) => ({
      ...prevInputStates,
      [key]: value,
    }));
  };

  const getDaysBetween = (
    fromDate,
    toDate,
    MS_IN_DAY,
    fromLeaveValue = "",
    toLeaveValue = "",
  ) => {
    if (!fromDate || !toDate) return 0;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (isNaN(start) || isNaN(end)) return 0;

    // Calculate normal day difference
    let applyingDays = Math.abs(Math.floor((end - start) / MS_IN_DAY)) + 1;

    // ----- Half Day Logic -----
    if (start.toDateString() === end.toDateString()) {
      // Single day leave
      if (
        fromLeaveValue === "First Half Day" ||
        fromLeaveValue === "Second Half Day"
      ) {
        applyingDays = 0.5;
      }
    } else {
      // Multi-day leave

      if (
        fromLeaveValue === "First Half Day" ||
        fromLeaveValue === "Second Half Day"
      ) {
        applyingDays -= 0.5;
      }

      if (
        toLeaveValue === "First Half Day" ||
        toLeaveValue === "Second Half Day"
      ) {
        applyingDays -= 0.5;
      }
    }

    return applyingDays;
  };

  const fetchLeaveManagementDetails = async () => {
    const whereConditions =
      selectedLeaveTypeId === 1
        ? `WHERE lm.employeeId=${_employeeId} AND lm.status=1`
        : `WHERE lm.status = 1 AND (
        (eod.appprovalManagerId = ${_employeeId} AND lm.approveManagerStatus != 1 AND lm.approveManagerStatus != 2)
        OR
        (eod.approvalLevel1Id = ${_employeeId} AND lm.approveManagerStatus != 0 AND lm.approveManagerStatus != 3 AND lm.approveLevel1 != 1 AND lm.approveLevel1 != 2)
        OR
        (eod.approvalLevel2Id = ${_employeeId} AND lm.approveLevel1 != 0 AND lm.approveLevel1 != 3 AND lm.approveLevel2 != 1 AND lm.approveLevel2 != 2)
      ) AND lm.status=1`;

    try {
      const leaveManagement = await fetchQuery(
        `SELECT lm.id,lm.documentNo,lm.applyLeaveDate,lm.leaveType,lm.leaveBalance,lm.fromDate,lm.toDate,lm.fromLeaveValue,lm.toLeaveValue,lm.remark,lm.docUrl,lm.employeeId,lm.approveManagerStatus,lm.reasonStatus,lm.rejectRemark,lm.approveLevel1,lm.approveLevel2,e.employeesCode AS employeesCode,e.name AS employeeName,eod.appprovalManagerId,CONCAT(em1.name,' (',em1.employeesCode,')') AS approvalManagerEmployee,eod.approvalLevel1Id,CONCAT(em2.name,' (',em2.employeesCode,')') AS approvalLevel1Employee,eod.approvalLevel2Id,CONCAT(em3.name,' (',em3.employeesCode,')') AS approvalLevel2Employee FROM leaveManagement lm LEFT JOIN employees e ON lm.employeeId=e.id LEFT JOIN employeeOtherData eod ON lm.employeeId=eod.employeeId LEFT JOIN employees em1 ON eod.appprovalManagerId=em1.id LEFT JOIN employees em2 ON eod.approvalLevel1Id=em2.id LEFT JOIN employees em3 ON eod.approvalLevel2Id=em3.id ${whereConditions} ORDER BY lm.id DESC`,
      );

      const MS_IN_DAY = 1000 * 60 * 60 * 24;
      const statusMap = {
        0: "Pending",
        1: "Pending",
        2: "Approved",
        3: "Rejected",
      };

      const updatedLeaveManagement = leaveManagement.map((item) => {
        const statuses = [
          item.approveManagerStatus,
          item.approveLevel1,
          item.approveLevel2,
        ].map(Number);
        // const leaveStatus = statuses.includes(3)
        //   ? statusMap[3]
        //   : statuses.reverse().includes(2)
        //   ? statusMap[2]
        //   : statusMap[0];

        const leaveStatus = statuses.includes(3)
          ? statusMap[3]
          : statusMap[item.approveLevel2] || item.approveLevel2;

        return {
          ...item,
          days: getDaysBetween(
            item.fromDate,
            item.toDate,
            MS_IN_DAY,
            item.fromLeaveValue,
            item.toLeaveValue,
          ),
          approveManagerStatus:
            statusMap[item.approveManagerStatus] || item.approveManagerStatus,
          approveLevel1: statusMap[item.approveLevel1] || item.approveLevel1,
          approveLevel2: statusMap[item.approveLevel2] || item.approveLevel2,
          leaveStatus,
        };
      });

      setLeaveManagementDetails(updatedLeaveManagement);
    } catch (error) {
      console.error("Error fetching leave management:", error);
    }
  };

  const fetchHolidayDetails = async () => {
    try {
      const holidayData = await fetchQuery(
        "select * from holidayRegister ORDER BY id DESC",
      );

      setHolidayList(holidayData);
    } catch (error) {
      console.error("Error fetching Holiday data:", error);
    }
  };

  const fetchPendingStatus = async () => {
    try {
      const result = await fetchQuery(
        `SELECT CASE WHEN EXISTS (SELECT 1 FROM leaveManagement WHERE approveManagerStatus=0 AND approveLevel1=0 AND employeeId=${_employeeId}) THEN TRUE ELSE FALSE END AS pendingStatus`,
      );

      setPendingStatus(result[0]?.pendingStatus == 1 ? true : false);
    } catch (error) {
      console.error("Error fetching pending status:", error);
    }
  };

  const reloadPage = () => {
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const onSubmit = () => {
    const fieldsToValidate = [
      {
        value: inputStates.applyLeaveDate,
        label: "Date of Application is required",
      },
      { value: inputStates.leaveType, label: "Leave Type is required" },
      { value: inputStates.fromDate, label: "From Date is required" },
      {
        value: inputStates.fromLeaveValue,
        label: "From Leave Value is required",
      },
      { value: inputStates.toDate, label: "Till Date is required" },
      {
        value: inputStates.toLeaveValue,
        label: "Till Leave Value is required",
      },
      { value: inputStates.employeeId, label: "Employee Name is required" },
    ];

    if (!validateRequiredFields(fieldsToValidate)) {
      return;
    }

    setDownlaodPdfLoading(true);
    const url = BASE_URL + `leaveManagement`;
    const isEdit = Boolean(params?.id);

    const request = isEdit
      ? axiosWithToken.patch(`${url}/${params.id}`, inputStates)
      : axiosWithToken.post(url, inputStates);

    request
      .then(() => {
        toast.success("LeaveManagement Created Successfully!");
        setDownlaodPdfLoading(false);
        reloadPage();
      })
      .catch((error) => {
        setDownlaodPdfLoading(false);
        console.error("Submit error:", error);
      });
  };

  const handleApprove = (row) => {
    setSelectedRow(row);

    setDialogProps({
      title: "Do you really want to Approve this?",
      showRemark: false,
      confirmText: "Approve",
      confirmColor: "success",

      onConfirm: async () => {
        const approvalMap = [
          { idKey: "appprovalManagerId", rowKey: "approveManagerStatus" },
          { idKey: "approvalLevel1Id", rowKey: "approveLevel1" },
          { idKey: "approvalLevel2Id", rowKey: "approveLevel2" },
        ];

        for (let i = 0; i < approvalMap.length; i++) {
          const { idKey, rowKey } = approvalMap[i];

          if (row[idKey] == _employeeId) {
            if (row[rowKey] !== "Approved") {
              await axiosWithToken.patch(
                BASE_URL + `leaveManagement/${row.id}`,
                { [rowKey]: 2 },
              );

              setOpenConfirm(false);
              reloadPage();
              break;
            }
          }
        }
      },
    });

    setOpenConfirm(true);
  };

  const handleReject = (row) => {
    setSelectedRow(row);

    setDialogProps({
      title: "Enter Rejection Remark",
      showRemark: true,
      confirmText: "Reject",
      confirmColor: "error",

      onConfirm: async () => {
        const currentRemark = remarkRef.current;

        const approvalMap = [
          { idKey: "approvalLevel2Id", rowKey: "approveLevel2" },
          { idKey: "approvalLevel1Id", rowKey: "approveLevel1" },
          { idKey: "appprovalManagerId", rowKey: "approveManagerStatus" },
        ];

        for (let i = 0; i < approvalMap.length; i++) {
          const { idKey, rowKey } = approvalMap[i];

          if (row[idKey] == _employeeId) {
            await axiosWithToken.patch(BASE_URL + `leaveManagement/${row.id}`, {
              [rowKey]: 3,
              rejectRemark: currentRemark,
            });

            setRemark("");
            setOpenConfirm(false);
            reloadPage();
            break;
          }
        }
      },
    });

    setOpenConfirm(true);
  };

  const handleModal = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const fetchLeaveDetails = async () => {
    try {
      const [Leave] = await fetchQuery(
        `SELECT * FROM leaveManagement WHERE id = ${params.id}`,
      );
      setInputStates(Leave);
    } catch (error) {
      console.error("Error fetching Holiday data:", error);
    }
  };

  useEffect(() => {
    if (Boolean(params?.id)) fetchLeaveDetails();
  }, [params]);

  useEffect(() => {
    fetchHolidayDetails();
  }, []);

  useEffect(() => {
    fetchLeaveManagementDetails();
  }, [selectedLeaveTypeId]);

  useEffect(() => {
    remarkRef.current = remark;
  }, [remark]);

  useEffect(() => {
    if (!_employeeId) return;
    fetchPendingStatus();

    const fetchEmployeeOtherData = async () => {
      try {
        const [employeeOtherData] = await fetchQuery(
          `select * from employees where id = ${_employeeId} AND status=1`,
        );
        setEmployeeData(employeeOtherData);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployeeOtherData();
  }, [_employeeId]);

  useEffect(() => {
    if (
      !inputStates?.leaveType
      // ||
      // !inputStates.fromDate ||
      // !inputStates.toDate ||
      // !inputStates.fromLeaveValue ||
      // !inputStates.toLeaveValue
    ) {
      return;
    }

    const calculateBalance = async () => {
      try {
        const { availableBalance, applyingDays } =
          await calculateAvailableBalance(
            inputStates.leaveType,
            leaveManagementDetails,
            weekOffs,
            inputStates.fromDate,
            inputStates.toDate,
            _employeeId,
            inputStates.fromLeaveValue,
            inputStates.toLeaveValue,
          );

        // Store both values in single state
        setLeaveData({
          availableBalance,
          applyingDays,
        });
        
    if (
      inputStates.leaveType !== "Casual Leave" &&
      (
        availableBalance <= 0 ||
        availableBalance < applyingDays
      )
    ) {
      toast.warning(
        `You cannot apply leave. ${inputStates.leaveType} is not available. You are applying Days are ${applyingDays}`,
        { autoClose: 5000 },
      );
    }

        updateInputObject("leaveBalance", availableBalance);
      } catch (err) {
        console.error("Error calculating leave balance", err);
      }
    };

    calculateBalance();
  }, [
    inputStates.leaveType,
    inputStates.fromDate,
    inputStates.toDate,
    inputStates.fromLeaveValue,
    inputStates.toLeaveValue,
    leaveManagementDetails,
    weekOffs,
    _employeeId,
  ]);

  useEffect(() => {
    if (selectedLeaveTypeId !== null) {
      localStorage.setItem(
        "selectedLeaveTypeId",
        selectedLeaveTypeId.toString(),
      );
    }
  }, [selectedLeaveTypeId]);

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {"Leave Request"}
      </Text>
      <br />
      <div className="  mt-5  ">
        <CommonForm
          inputStates={inputStates}
          updateInputObject={updateInputObject}
          inputFields={inputFields}
          // colSpan={[1, 2, 2, 2]}
        />
        <br />
        <div className="flex gap-10 mt-5 justify-end ">
          <CancelButton
            id="btn-cancel"
            label="Cancel"
            outline
            onClick={() => {
              if (window.confirm("Are you sure you want to cancel?")) {
                window.history.back();
              }
            }}
          />
          {(permissions.save || permissions.allAccess) && (
            <SaveAndUpdate
              id="btn-add-outline"
              label={Boolean(params?.id) ? "Update" : "Save"}
              onClick={onSubmit}
              disabled={
                (inputStates.leaveType !== "Casual Leave" &&
                  (!inputStates?.leaveBalance ||
                    inputStates.leaveBalance === 0 ||
                    leaveData.availableBalance < leaveData.applyingDays)) ||
                _employeeId !== inputStates?.employeeId
              }
            />
          )}
        </div>
      </div>

      <br />

      <br />

      {(permissions.approve || permissions.allAccess) && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Autocomplete
            size="small"
            options={leaveOptions}
            value={
              leaveOptions.find(
                (option) => option.id === selectedLeaveTypeId,
              ) || null
            }
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) =>
              setSelectedLeaveTypeId(newValue ? newValue.id : null)
            }
            sx={{ width: 250 }}
            renderInput={(params) => (
              <TextField {...params} label="Select Leave Type" />
            )}
          />
        </div>
      )}

      <br />

      <ListView
        headers={headers}
        data={leaveManagementDetails}
        actions={{
          view: (row) => window.open(`${row.docUrl}`, "_blank"),
          modal: handleModal,
          ...(selectedLeaveTypeId == 1 && {
            edit: handleEdit,
            delete: handleDelete,
          }),
        }}
        // showCheckbox={true}
        // deleteTableName={"leaveManagement/records"}
        moduleId={props.moduleId}
        totalRows={leaveManagementDetails.length}
      />
      {downloadPdfloading && <LoaderModal loading={downloadPdfloading} />}

      <div>
        {/* Example button to trigger modal */}

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Leave Status{" "}
            {selectedRow
              ? `Of ${selectedRow?.employeeName} (${selectedRow?.employeesCode})`
              : ""}
            <IconButton onClick={handleClose} sx={{ color: "red" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <ListView
              headers={approveHeaders}
              data={selectedRow ? [selectedRow] : []}
              actions={{
                approve: handleApprove,
                reject: handleReject,
              }}
              // disabled={selectedRow?.leaveStatus === "Rejected"}
              headerEmployee={true}
              moduleId={props.moduleId}
              headerEmployeeKeys={[
                "approvalManagerEmployee",
                "approvalLevel1Employee",
                "approvalLevel2Employee",
                "",
                "",
              ]}
              _employeeId={_employeeId}
            />
          </DialogContent>
        </Dialog>
      </div>
      <AppConfirmBox
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        remark={remark}
        setRemark={setRemark}
        {...dialogProps}
      />
    </div>
  );
};

export default ApplyLeaveUI;
